import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import QRCode from 'qrcode';
import { ApiService } from '../../core/api.service';
import {
  DocumentExtraction,
  Machine,
  MachineDocument,
  MaintenancePlan,
  MaintenanceTask,
} from '../../core/models';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-machine-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './machine-detail.component.html',
  styleUrl: './machine-detail.component.scss',
})
export class MachineDetailComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);

  machine = signal<Machine | null>(null);
  documents = signal<MachineDocument[]>([]);
  plans = signal<MaintenancePlan[]>([]);
  tasks = signal<MaintenanceTask[]>([]);
  extractions = signal<DocumentExtraction[]>([]);
  qrDataUrl = signal('');
  message = signal('');

  planForm = {
    title: '',
    instructions: '',
    frequency_value: 6,
    frequency_unit: 'months',
    estimated_minutes: 30,
  };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id')!;
    this.load(id);
  }

  load(id: string): void {
    this.api.get<Machine>(`/api/machines/${id}`).subscribe(async (m) => {
      this.machine.set(m);
      this.qrDataUrl.set(await QRCode.toDataURL(m.qr_token, { width: 220, margin: 1 }));
    });
    this.api.get<MachineDocument[]>(`/api/machines/${id}/documents`).subscribe((d) => this.documents.set(d));
    this.api.get<MaintenancePlan[]>('/api/plans', { machine_id: id }).subscribe((p) => this.plans.set(p));
    this.api.get<MaintenanceTask[]>('/api/tasks', { machine_id: id }).subscribe((t) => this.tasks.set(t));
    this.api
      .get<DocumentExtraction[]>('/api/extractions', { machine_id: id })
      .subscribe((e) => this.extractions.set(e));
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const m = this.machine();
    if (!file || !m) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('description', 'Manuale costruttore');
    this.api.upload<MachineDocument>(`/api/machines/${m.id}/documents`, fd).subscribe((doc) => {
      this.message.set(`Caricato: ${doc.filename}`);
      this.load(m.id);
    });
  }

  startExtraction(docId: string): void {
    const m = this.machine();
    if (!m) return;
    this.api.post<DocumentExtraction>(`/api/extractions/from-document/${docId}`).subscribe(() => {
      this.message.set('Estrazione AI avviata. Vai a Review AI per approvare.');
      setTimeout(() => this.load(m.id), 1500);
    });
  }

  createPlan(): void {
    const m = this.machine();
    if (!m || !this.planForm.title) return;
    this.api
      .post<MaintenancePlan>(`/api/plans?machine_id=${m.id}`, {
        ...this.planForm,
        is_approved: true,
        is_active: true,
      })
      .subscribe(() => {
        this.planForm.title = '';
        this.planForm.instructions = '';
        this.load(m.id);
      });
  }

  fieldUrl(token: string): string {
    return `${environment.apiUrl.replace(':8000', ':4300')}/m/${token}`;
  }
}
